import React, { useState } from 'react';
import { Check, X, ClipboardCheck, MapPin, MessageSquareWarning, History, Loader2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { StepUpDialog } from './StepUpDialog';
import { VisitGallery } from './VisitGallery';
import { useAuth } from '../context/AuthContext';

const CHECKS = [
  ['identity_match', 'Identidad coincide con el DNI/CE'],
  ['documents_legible', 'Documentos legibles y vigentes'],
  ['ruc_match', 'RUC / razón social coinciden (o N/A persona natural)'],
  ['contact_verified', 'Correo y teléfono de contacto verificados'],
];
const REVIEW_LABEL = { in_review: 'En revisión', corrections_requested: 'Correcciones solicitadas', preapproved: 'Preaprobada' };
const err = (e) => toast.error(e?.response?.data?.detail || 'Operación rechazada');
const inputCls = 'mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm';

const SiteVisitForm = ({ userId, initial, onSaved }) => {
  const [v, setV] = useState(initial || { visited_on: new Date().toISOString().slice(0, 10), visitor_name: '',
    address_verified: false, store_exists: false, products_verified: false, products_observed: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/kyc/${userId}/site-visit`, v); toast.success('Visita registrada'); onSaved(); } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <div className="bg-black/40 border border-gray-800 rounded-lg p-4 space-y-3" data-testid={`site-visit-form-${userId}`}>
      <p className="text-white text-sm font-medium flex items-center gap-2"><MapPin className="w-4 h-4 text-[#FF6B35]" /> Visita al lugar</p>
      <div className="grid md:grid-cols-2 gap-3">
        <label className="text-xs text-gray-400">Fecha de visita<input type="date" value={v.visited_on} onChange={set('visited_on')} className={inputCls} data-testid="site-visit-date" /></label>
        <label className="text-xs text-gray-400">Responsable de la visita<input value={v.visitor_name} onChange={set('visitor_name')} className={inputCls} data-testid="site-visit-visitor" /></label>
      </div>
      {[['address_verified', 'La dirección coincide con la declarada'], ['store_exists', 'La tienda existe físicamente'], ['products_verified', 'Se verificaron los productos que vende']].map(([k, l]) => (
        <label key={k} className="flex items-center gap-2 text-sm text-gray-300">
          <input type="checkbox" checked={!!v[k]} onChange={set(k)} data-testid={`site-visit-${k}`} /> {l}
        </label>
      ))}
      <label className="block text-xs text-gray-400">Productos observados<textarea rows={2} value={v.products_observed} onChange={set('products_observed')} className={inputCls} data-testid="site-visit-products" /></label>
      <label className="block text-xs text-gray-400">Notas<textarea rows={2} value={v.notes || ''} onChange={set('notes')} className={inputCls} data-testid="site-visit-notes" /></label>
      <Button size="sm" onClick={save} disabled={busy} className="bg-[#FF6B35] hover:bg-[#e55a2a]" data-testid="site-visit-save">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar visita'}
      </Button>
    </div>
  );
};

const VisitEvidence = ({ s, canUpload, reload }) => {
  const [busy, setBusy] = useState(false);
  const photos = s.site_visit_evidence || [];
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try { await api.post(`/admin/kyc/${s.user_id}/site-visit/evidence`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Foto de la visita guardada'); reload(); } catch (x) { err(x); } finally { setBusy(false); }
  };
  return (
    <div className="text-xs text-gray-400 space-y-1" data-testid={`site-visit-evidence-${s.user_id}`}>
      <p className="flex items-center gap-2"><Camera className="w-3 h-3" /> Evidencia fotográfica de la visita:
        {photos.length === 0 && <span className="text-yellow-500" data-testid="site-visit-evidence-missing">sin fotos</span>}</p>
      <VisitGallery photos={photos} />
      {canUpload && (
        <label className="inline-flex items-center gap-2 cursor-pointer text-white bg-gray-800 hover:bg-gray-700 rounded px-2 py-1" data-testid={`site-visit-evidence-upload-${s.user_id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />} Subir foto (JPG/PNG, máx. 5MB)
          <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={upload} disabled={busy} data-testid={`site-visit-evidence-input-${s.user_id}`} />
        </label>
      )}
    </div>
  );
};

const FinalDecision = ({ s, ready, ask }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState('');
  const canApprove = ready && confirmed;
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm text-gray-300">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={!ready} data-testid={`superadmin-visit-confirm-${s.user_id}`} />
        Confirmo la visita al lugar (revisé el registro y la evidencia fotográfica)
      </label>
      {confirmed && <textarea rows={2} maxLength={2000} placeholder="Nota sobre la visita (opcional)" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} data-testid={`superadmin-visit-note-${s.user_id}`} />}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" disabled={!canApprove} className="bg-green-600 hover:bg-green-700" data-testid={`approve-seller-${s.user_id}`}
          onClick={() => ask('Aprobación definitiva (SuperAdmin)', (x) => api.put(`/admin/sellers/${s.user_id}/status`, { status: 'approved', reason: x.reason, site_visit_confirmed: true, site_visit_note: note.trim() || null }))}>
          <Check className="w-4 h-4 mr-1" /> Aprobar definitivamente</Button>
        <Button size="sm" className="bg-red-600 hover:bg-red-700" data-testid={`reject-seller-${s.user_id}`}
          onClick={() => ask('Rechazo definitivo (SuperAdmin, se enviará al vendedor)', (x) => api.put(`/admin/sellers/${s.user_id}/status`, { status: 'rejected', reason: x.reason }))}>
          <X className="w-4 h-4 mr-1" /> Rechazar</Button>
        {!ready && <span className="text-xs text-gray-500">Requiere preaprobación del Revisor KYC, visita al lugar confirmada y foto de evidencia.</span>}
      </div>
    </div>
  );
};

const ReviewerTools = ({ s, review, reload, ask }) => {
  const [checks, setChecks] = useState(() => Object.fromEntries(CHECKS.map(([k]) => [k, !!review.checks?.[k]])));
  const [notes, setNotes] = useState(review.notes || '');
  const [showVisit, setShowVisit] = useState(false);
  const saveChecks = async () => {
    try { await api.put(`/admin/kyc/${s.user_id}/review`, { checks, notes: notes || null }); toast.success('Revisión guardada'); reload(); } catch (e) { err(e); }
  };
  return (
    <div className="space-y-3">
      <div className="grid md:grid-cols-2 gap-2">
        {CHECKS.map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={checks[k]} onChange={(e) => setChecks((p) => ({ ...p, [k]: e.target.checked }))} data-testid={`kyc-check-${k}-${s.user_id}`} /> {l}
          </label>
        ))}
      </div>
      <textarea rows={2} placeholder="Notas de la revisión" value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} data-testid={`kyc-notes-${s.user_id}`} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={saveChecks} data-testid={`kyc-save-review-${s.user_id}`}><ClipboardCheck className="w-4 h-4 mr-1" /> Guardar revisión</Button>
        <Button size="sm" variant="outline" onClick={() => setShowVisit((v) => !v)} data-testid={`kyc-toggle-visit-${s.user_id}`}><MapPin className="w-4 h-4 mr-1" /> Visita al lugar</Button>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid={`kyc-preapprove-${s.user_id}`}
          onClick={() => ask('Preaprobar solicitud', (x) => api.post(`/admin/kyc/${s.user_id}/preapprove`, { notes: x.reason }))}>Preaprobar</Button>
        <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700" data-testid={`kyc-corrections-${s.user_id}`}
          onClick={() => ask('Solicitar correcciones al vendedor (se le enviará por correo)', (x) => api.post(`/admin/kyc/${s.user_id}/request-corrections`, { message: x.reason }))}>
          <MessageSquareWarning className="w-4 h-4 mr-1" /> Solicitar correcciones</Button>
      </div>
      {showVisit && <SiteVisitForm userId={s.user_id} initial={review.site_visit} onSaved={() => { setShowVisit(false); reload(); }} />}
    </div>
  );
};

const Summary = ({ review }) => {
  const visit = review.site_visit;
  return (
    <div className="text-xs text-gray-400 space-y-1" data-testid="kyc-review-summary">
      <p>Estado de revisión: <span className="text-white" data-testid="kyc-review-status">{REVIEW_LABEL[review.review_status] || 'Sin revisar'}</span>
        {review.reviewer_email && ` · revisó ${review.reviewer_email}`}
        {review.preapproved_by_email && ` · preaprobó ${review.preapproved_by_email} (${new Date(review.preapproved_at).toLocaleString('es-PE')})`}</p>
      {visit ? (
        <p data-testid="kyc-site-visit-summary">Visita {visit.visited_on} por {visit.visitor_name}: dirección {visit.address_verified ? '✓' : '✗'} · tienda existe {visit.store_exists ? '✓' : '✗'} · productos verificados {visit.products_verified ? '✓' : '✗'} — {visit.products_observed}
          {review.site_visit_by_email && ` (registró ${review.site_visit_by_email})`}
          {visit.confirmed_at && <span data-testid="kyc-site-visit-confirmed"> · confirmada por SuperAdmin el {new Date(visit.confirmed_at).toLocaleString('es-PE')}{visit.confirmation_note ? ` — ${visit.confirmation_note}` : ''}</span>}</p>
      ) : <p className="text-yellow-500">Visita al lugar pendiente</p>}
    </div>
  );
};

export const KycReviewBox = ({ s, reload }) => {
  const { can } = useAuth();
  const [dialog, setDialog] = useState(null);
  const [history, setHistory] = useState(null);
  const review = s.kyc_review || {};
  const visitOk = review.site_visit?.store_exists && review.site_visit?.products_verified;
  const readyToApprove = review.review_status === 'preapproved' && visitOk && (s.site_visit_evidence || []).length > 0;
  const ask = (title, run) => setDialog({ title, run });
  const submit = async (x) => {
    try { await dialog.run(x); toast.success('Operación registrada'); reload(); return true; } catch (e) { err(e); return false; }
  };
  const loadHistory = () => api.get(`/admin/kyc/${s.user_id}/history`).then((r) => setHistory(r.data)).catch(err);

  return (
    <div className="mt-4 border-t border-gray-800 pt-4 space-y-3" data-testid={`kyc-review-${s.user_id}`}>
      {(s.vendor_status === 'pending' || s.kyc_review) && <Summary review={review} />}
      {s.vendor_status === 'pending' && can('kyc.review') && <ReviewerTools s={s} review={review} reload={reload} ask={ask} />}
      {(s.vendor_status === 'pending' || (s.site_visit_evidence || []).length > 0) && (
        <VisitEvidence s={s} canUpload={s.vendor_status === 'pending' && can('kyc.review')} reload={reload} />
      )}
      {s.vendor_status === 'pending' && can('kyc.decide') && <FinalDecision s={s} ready={readyToApprove} ask={ask} />}
      <button className="text-xs text-gray-400 hover:text-white flex items-center gap-1" onClick={loadHistory} data-testid={`kyc-history-btn-${s.user_id}`}>
        <History className="w-3 h-3" /> Ver historial
      </button>
      {history && (
        <ul className="text-xs text-gray-500 space-y-1" data-testid={`kyc-history-${s.user_id}`}>
          {history.map((h, i) => <li key={i}>{new Date(h.created_at).toLocaleString('es-PE')} · {h.event} · {h.actor_email || '—'}{h.reason ? ` · ${h.reason}` : ''}</li>)}
        </ul>
      )}
      <StepUpDialog open={!!dialog} title={dialog?.title} needTotp={false} onSubmit={submit} onClose={() => setDialog(null)} />
    </div>
  );
};

export default KycReviewBox;
